<template>
  <section class="messages-workspace">
    <div class="workspace-grid" :class="{ 'list-collapsed': listCollapsed }">
      <aside>
        <button class="list-toggle" type="button" :aria-expanded="!listCollapsed" :aria-label="listCollapsed ? 'Expand client list' : 'Collapse client list'" :title="listCollapsed ? 'Expand client list' : 'Collapse client list'" @click="listCollapsed = !listCollapsed">{{ listCollapsed ? '»' : '«' }}</button>
        <div v-show="!listCollapsed">
        <input v-model="search" type="search" placeholder="Search for App Client" aria-label="Search for App Client">
        <button class="refresh" :disabled="loading" @click="refreshConsole">{{ loading ? 'Loading…' : 'Refresh inbox' }}</button>
        <nav class="archive-filter" aria-label="Conversation view">
          <button :class="{ active: conversationView === 'active' }" @click="conversationView = 'active'">Inbox</button>
          <button :class="{ active: conversationView === 'archived' }" @click="conversationView = 'archived'">Archived</button>
        </nav>
        <p v-if="error" role="alert">{{ error }}</p>
        </div>
        <button v-for="client in filteredClients" :key="client.id" :class="['client', { selected: selected === client.id }]" :aria-label="client.name" :title="listCollapsed ? client.name : undefined" :aria-pressed="selected === client.id" @click="selectConversation(client)">
          <span v-if="listCollapsed" class="initial-avatar" aria-hidden="true">{{ initials(client.name) }}</span>
          <template v-else>
          <span class="conversation-heading"><strong>{{ client.name }}</strong><time>{{ formatDate(client.occurredAt) }}</time></span>
          <span class="preview">{{ client.incoming ? '' : 'You: ' }}{{ client.preview }}</span>
          <small v-if="client.smsThreads?.some(thread => thread.unreadCount)">Unread texts · </small>
          <small>{{ client.channel === 'sms' ? 'Text' : client.channel === 'email' ? 'Email' : client.channel === 'app' ? 'App' : client.channel.replace('sms', 'Text').replace('email', 'Email') }}</small>
          </template>
        </button>
        <p v-if="!loading && !filteredClients.length">No matching conversations.</p>
      </aside>
      <main>
        <MessageTextTools ref="unassignedPane" v-if="selectedClient?.threadId" :key="'tools-' + selected" :threads="selectedClient.smsThreads || []" unassigned @changed="loadClients">
          <template #tools><button :disabled="archiving" @click="archiveConversation">{{ selectedClient.conversationStatus === 'archived' ? 'Move to Inbox' : 'Archive conversation' }}</button></template>
        </MessageTextTools>
        <MaixClientMessages ref="messagePane" v-if="selectedClient && !selectedClient.threadId" :key="selected" :client-id="selected" show-request-links console-layout @open-request="openRequest" @message-sent="loadClients">
          <template #tools>
            <button :disabled="archiving" @click="archiveConversation">{{ selectedClient.conversationStatus === 'archived' ? 'Move to Inbox' : 'Archive conversation' }}</button>
            <MessageTextTools :threads="selectedClient.smsThreads || []" @changed="loadClients" />
          </template>
        </MaixClientMessages><p v-if="!selectedClient" class="empty">Select a conversation to review app, email, and text messages.</p></main>
    </div>
  </section>
</template>
<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import MaixClientMessages from "./MaixClientMessages.vue";
import MessageTextTools from "./MessageTextTools.vue";
import { fetchMaixMessageClients, fetchAttorneyRequests, markMaixTextThreadRead, setMaixConversationArchived } from "@/firebase/firestore";
export default {
  name: "MaixMessagesDashboard",
  components: { MaixClientMessages, MessageTextTools },
  props: { adminId: { type: String, required: true }, adminName: { type: String, default: "" } },
  emits: ["open-request"],
  setup(props, { emit }) {
    const listCollapsed = ref(false);
    const initials = name => {
      const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
      return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : (parts[0] || "?").slice(0, 2).toUpperCase();
    };
    const messagePane = ref(null), unassignedPane = ref(null);
    let disposed = false;
    const conversationView = ref("active"); const archiving = ref(false); const clients = ref([]); const selected = ref("");
    const search = ref(""); const loading = ref(false); const error = ref("");
    const selectedClient = computed(() => clients.value.find(client => client.id === selected.value));
    const filteredClients = computed(() => clients.value.filter(client => (client.conversationStatus || "active") === conversationView.value &&
      client.name.toLowerCase().includes(search.value.toLowerCase())));
    watch([filteredClients, conversationView], () => {
      if (!filteredClients.value.some(client => client.id === selected.value)) selected.value = filteredClients.value[0]?.id || "";
    });
    const archiveConversation = async () => {
      const client = selectedClient.value;
      if (!client || archiving.value) return;
      archiving.value = true; error.value = "";
      try {
        await setMaixConversationArchived({ ...(client.threadId ? { threadId: client.threadId } : { clientId: client.id }), archived: client.conversationStatus !== "archived" });
        await loadClients();
      } catch (failure) { error.value = failure.message || "Unable to archive the conversation."; }
      finally { archiving.value = false; }
    };
    const selectConversation = async client => {
      selected.value = client.id;
      try {
        await Promise.all((client.smsThreads || []).filter(thread => thread.unreadCount).map(thread => markMaixTextThreadRead(thread.id)));
        for (const thread of client.smsThreads || []) thread.unreadCount = 0;
      } catch (failure) { error.value = failure.message || "Unable to mark texts read."; }
    };
    const loadClients = async () => {
      if (loading.value || disposed) return;
      loading.value = true; error.value = "";
      try {
        const result = await fetchMaixMessageClients();
        if (disposed) return;
        clients.value = result;
        if (!result.some(client => client.id === selected.value)) selected.value = result[0]?.id || "";
      }
      catch (failure) { error.value = failure?.message || "Unable to load the inbox."; }
      finally { loading.value = false; }
    };
    const refreshConsole = async () => {
      const previous = selected.value;
      await loadClients();
      if (disposed) return;
      await nextTick();
      if (selected.value === previous) await (messagePane.value || unassignedPane.value)?.refresh();
    };
    const openRequest = async requestId => {
      error.value = "";
      try {
        const request = (await fetchAttorneyRequests()).find(item => item.id === requestId);
        if (disposed) return;
        if (!request) throw new Error("This attorney request is no longer available.");
        emit("open-request", request);
      } catch (failure) { if (!disposed) error.value = failure?.message || "Unable to open this request."; }
    };
    const formatDate = value => new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    onMounted(loadClients);
    onBeforeUnmount(() => { disposed = true; });
    return { listCollapsed, initials, messagePane, unassignedPane, refreshConsole, conversationView, archiving, archiveConversation, selectedClient, selectConversation, selected, search, loading, error, filteredClients, loadClients, openRequest, formatDate };
  }
};
</script>
<style scoped>
.messages-workspace { width: 100%; height: 100%; display: flex; flex-direction: column; min-height: 0; }
.archive-filter { display: flex; align-items: center; gap: 8px; margin-top: 12px; font-size: .8rem; }
.archive-filter button { flex: 1; }
.conversation-actions { flex: 0 0 auto; padding: 8px 14px; display: flex; justify-content: flex-end; }
button { border: 0; padding: 12px 16px; border-radius: 8px; cursor: pointer; background: #edf4ef; color: #315d45; }
.active, .selected { background: #d6f1e1; font-weight: 700; }
.workspace-grid { overflow: hidden; display: grid; grid-template-columns: 320px minmax(0, 1fr); gap: 14px; padding: 4px; flex: 1; min-height: 0; }
aside { padding: 10px 16px 16px; overflow: auto; border: 1px solid #dce6e0; border-radius: 16px; background: white; box-shadow: 0 3px 12px rgba(36, 60, 50, .04); } input { width: 100%; box-sizing: border-box; padding: 10px; border: 1px solid #dce6e0; border-radius: 8px; }
.conversation-heading { display: flex; justify-content: space-between; gap: 10px; }
.conversation-heading time { flex-shrink: 0; font-size: .75rem; font-weight: 400; }
.preview { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 8px 0; font-weight: 400; }
.client small { font-weight: 400; }
.client, .refresh { width: 100%; text-align: left; margin-top: 10px; } main { display: flex; flex-direction: column; min-height: 0; min-width: 0; overflow: hidden; padding: 0; } .empty { padding: 30px; color: #65796c; }
@media (max-width: 800px) { .workspace-grid { grid-template-columns: 1fr; } aside { max-height: 140px; } .workspace-grid { grid-template-rows: auto minmax(0, 1fr); } }

.list-toggle { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; box-sizing: border-box; margin: 0 0 14px auto; padding: 0; font-size: 24px; line-height: 1; }
.workspace-grid.list-collapsed { grid-template-columns: 80px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); }
.list-collapsed aside { padding: 10px 8px; max-height: none; }
.list-collapsed .list-toggle { margin: 0 auto 12px; }
.list-collapsed .client { padding: 10px 4px; display: flex; justify-content: center; }
.initial-avatar { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(145deg, #adc9dc, #858fc4); color: white; font-size: 17px; font-weight: 700; }
.list-collapsed .client.selected { background: #16794f; }
</style>
